/**
 * One place to turn "the API said no" into copy a person can actually read.
 *
 * The API used to answer an unverified-email request with HTML (`<div style="text-align: center;">…`,
 * `<br/>`, `<a href="mailto:…">`) and every screen that printed `err.message` showed the tags
 * verbatim. Payloads are plain text now — `title` / `message` / `hint` / `supportEmail` (see
 * `VLEnsureEmailIsVerified` and `AuthController::login`) — but deployed or third-party responses
 * still carry markup, so messages are flattened here and rendered as text (with real mailto links)
 * by `ApiErrorAlert`.
 */
import { ApiError } from '@/lib/api/apiClient';

/** Shared inbox for "I'm stuck" moments — the API sends the same address in `supportEmail`. */
export const SUPPORT_EMAIL = 'support@listaa.com';

const NAMED_ENTITIES: Record<string, string> = {
  amp: '&',
  apos: "'",
  gt: '>',
  lt: '<',
  nbsp: ' ',
  quot: '"',
};

function decodeEntities(text: string): string {
  return text.replace(/&(#x?[0-9a-f]+|[a-z]+);/gi, (match, name: string) => {
    const key = name.toLowerCase();
    const named = NAMED_ENTITIES[key];
    if (named) return named;

    const code = key.startsWith('#x')
      ? parseInt(key.slice(2), 16)
      : key.startsWith('#')
        ? parseInt(key.slice(1), 10)
        : Number.NaN;

    return Number.isFinite(code) && code > 0 && code <= 0x10ffff
      ? String.fromCodePoint(code)
      : match;
  });
}

/**
 * Flattens a server message into readable plain text: block tags become blank lines (paragraphs
 * survive), `<br>` becomes a newline, everything else is dropped and entities are decoded.
 */
export function plainApiMessage(raw: string): string {
  return decodeEntities(
    raw
      .replace(/<\s*br\s*\/?\s*>/gi, '\n')
      .replace(/<\s*\/\s*(p|div|li|ul|ol|h[1-6]|tr|table)\s*>/gi, '\n\n')
      .replace(/<[^>]*>/g, '')
  )
    .replace(/[^\S\n]+/g, ' ') // collapse runs of spaces/tabs, keep the newlines
    .replace(/ *\n */g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** First email address mentioned in a message — "Contact us: support@listaa.com" → setup hook. */
export function findEmailAddress(text: string): string | undefined {
  return text.match(/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/i)?.[0];
}

type ApiErrorPayload = {
  error?: unknown;
  error_code?: unknown;
  message?: unknown;
  title?: unknown;
  hint?: unknown;
  supportEmail?: unknown;
};

type ReadError = {
  /** HTTP status when the failure came from the API; `undefined` for local/unknown errors. */
  status?: number;
  payload: ApiErrorPayload;
  message: string;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function readError(error: unknown): ReadError {
  if (error instanceof ApiError) {
    return {
      status: error.status,
      payload: isRecord(error.data) ? (error.data as ApiErrorPayload) : {},
      message: typeof error.message === 'string' ? error.message : '',
    };
  }
  if (error instanceof Error) {
    return { payload: {}, message: error.message };
  }
  if (typeof error === 'string') {
    return { payload: {}, message: error };
  }
  return { payload: {}, message: '' };
}

/**
 * `true` when the API refused the request because the account's email isn't verified yet.
 *
 * HTTP 423 is used for nothing else on this API; the message check keeps older/HTML payloads
 * (which had no `error` code on some endpoints) detectable too.
 */
export function isEmailNotVerifiedError(error: unknown): boolean {
  const { status, payload, message } = readError(error);

  if (payload.error === 'email_not_verified' || payload.error_code === 'email_not_verified') return true;
  if (status === 423) return true;
  return /verify your email|email not verified|not verified yet/i.test(message);
}

export type ApiErrorDetails = {
  /** The account must confirm its email before this action is allowed. */
  emailNotVerified: boolean;
  title?: string;
  message: string;
  hint?: string;
  /** Address a "Contact support" action should open. */
  supportEmail: string;
  /** HTTP status when the API answered; `undefined` for offline/local failures. */
  status?: number;
};

/**
 * Describe any thrown value (ApiError, Error, string) for `ApiErrorAlert`.
 *
 * Server copy wins over the local fallbacks; `title` is left undefined when nothing meaningful can
 * be said about the failure, so plain client-side validation messages stay one-liners.
 */
export function describeApiError(
  error: unknown,
  options: { title?: string; hint?: string } = {}
): ApiErrorDetails {
  const { status, payload, message: rawMessage } = readError(error);
  const emailNotVerified = isEmailNotVerifiedError(error);

  const serverTitle = typeof payload.title === 'string' ? plainApiMessage(payload.title) : '';
  const serverHint = typeof payload.hint === 'string' ? plainApiMessage(payload.hint) : '';

  const message =
    plainApiMessage(rawMessage) ||
    (emailNotVerified
      ? 'Please verify your email address before continuing — we emailed you a verification link.'
      : 'Something went wrong on our side. Please try again in a moment.');

  const supportEmail =
    (typeof payload.supportEmail === 'string' && findEmailAddress(payload.supportEmail)) ||
    findEmailAddress(`${message} ${serverHint} ${options.hint ?? ''}`) ||
    SUPPORT_EMAIL;

  const hint =
    serverHint ||
    options.hint ||
    (emailNotVerified
      ? `Didn't get the link? Resend it from the verification page and check your spam folder — or email ${supportEmail}.`
      : undefined);

  return {
    emailNotVerified,
    // A 423 always leads with the one step that's missing, even when the response carried no title
    // (legacy HTML payloads) — "We couldn't load your venue" describes the symptom, not the fix.
    title: serverTitle || (emailNotVerified ? 'Verify your email address' : options.title),
    message,
    hint,
    supportEmail,
    status,
  };
}
